/**
 * SmartBooker · Feature Access Hooks (frontend)
 *
 * Reads the tenant plan snapshot from `/api/me/features` and provides:
 *   - useFeatureSnapshot()  → full snapshot { plan, vertical, features, ... }
 *   - useHasFeature(key)    → boolean (returns true while loading to avoid flicker)
 *   - useVertical()         → "dental" | "wellness" | "estetica" | "hair" | "beauty" | "generic"
 *
 * The snapshot is cached at module level and refreshed on auth changes.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";

let _cache = null;       // last snapshot
let _inflight = null;    // in-flight promise (dedup)
const _listeners = new Set();

function _notify() {
  _listeners.forEach((fn) => {
    try { fn(_cache); } catch { /* noop */ }
  });
}

export async function refreshFeatureSnapshot() {
  if (_inflight) return _inflight;
  _inflight = axios
    .get(`${API}/auth/me/features`, { withCredentials: true })
    .then((r) => { _cache = r.data || null; return _cache; })
    .catch(() => { _cache = null; return null; })
    .finally(() => { _inflight = null; _notify(); });
  return _inflight;
}

export function useFeatureSnapshot() {
  const { user } = useAuth();
  const [snap, setSnap] = useState(_cache);

  useEffect(() => {
    if (!user) { setSnap(null); _cache = null; return; }
    if (_cache) setSnap(_cache); else refreshFeatureSnapshot().then(setSnap);
    const listener = (next) => setSnap(next);
    _listeners.add(listener);
    return () => _listeners.delete(listener);
  }, [user]);

  return snap;
}

export function useHasFeature(featureKey) {
  const snap = useFeatureSnapshot();
  // Loading → return true (optimistic, prevents flicker on protected UI)
  if (!snap) return true;
  if (!featureKey) return true;
  const features = snap.features || [];
  return features.includes(featureKey);
}

export function useVertical() {
  const snap = useFeatureSnapshot();
  return (snap && snap.vertical) || null;
}

export function usePlanSnapshot() {
  return useFeatureSnapshot();
}
